import { useSearch } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useTenantLanguages } from '@/entities/tenant'
import { resolveDefaultLanguages } from '@/shared/utils/language-utils'

/** Query string the POI form understands (`?lang=vi-south`). It is user-editable, so it is validated before use. */
interface PoiFormSearch {
  lang?: string
}

/**
 * Languages of the POI form.
 *
 * VI: Ngôn ngữ mở form lấy từ `?lang=` (khi nó nằm trong ngôn ngữ tenant hỗ trợ), nếu không thì ngôn ngữ mặc định của tenant. Link từ danh sách POI truyền `lang` để mở đúng tab đang xem. | EN: The opening language comes from `?lang=` (when the tenant supports it), otherwise the tenant default. Links from the POI list pass `lang` so the form opens on the tab being viewed.
 */
export function usePoiFormLanguage(tenantId: string) {
  const requestedLanguageCode = useSearch({
    strict: false,
    select: (search: PoiFormSearch) => (typeof search.lang === 'string' ? search.lang.trim() : ''),
  })
  const tenantLanguagesQuery = useTenantLanguages(tenantId)
  const tenantLanguageItems = tenantLanguagesQuery.items

  const supportedLanguages = useMemo(
    () => resolveDefaultLanguages(tenantLanguageItems),
    [tenantLanguageItems],
  )
  const tenantDefaultLanguage = useMemo(() => {
    if (!tenantLanguageItems.length) {
      return ''
    }
    return (
      tenantLanguageItems.find((item) => item.isDefault)?.languageCode ??
      supportedLanguages[0] ??
      ''
    )
  }, [supportedLanguages, tenantLanguageItems])
  const formLanguageCode = useMemo(() => {
    if (requestedLanguageCode && supportedLanguages.includes(requestedLanguageCode)) {
      return requestedLanguageCode
    }
    return tenantDefaultLanguage
  }, [requestedLanguageCode, supportedLanguages, tenantDefaultLanguage])

  return {
    supportedLanguages,
    tenantDefaultLanguage,
    formLanguageCode,
    isLanguagesFetched: tenantLanguagesQuery.isFetched,
  }
}
